import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { EMPTY_DOCTOR_FORM } from "../../utils/doctorHelpers";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoctorFormModal({
  open,
  onOpenChange,
  token,
  initialForm,
  editingDoctor,
  onSaved,
  title = "Nuovo Medico",
  /** Prefisso API (default struttura). Es. `/api/oss/me/doctors` per area OSS. */
  apiBase = "/api/doctors",
}) {
  const [form, setForm] = useState(initialForm || EMPTY_DOCTOR_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm || EMPTY_DOCTOR_FORM);
    }
  }, [open, initialForm, editingDoctor?.id]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleOpenChange = (next) => {
    if (!next) {
      setForm(initialForm || EMPTY_DOCTOR_FORM);
    }
    onOpenChange(next);
  };

  const handleSave = async () => {
    const name = String(form.name || "").trim();
    if (!name) {
      toast.error("Nome e cognome obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        specialization: form.specialization?.trim() || "",
        phone: form.phone?.trim() || "",
        email: form.email?.trim() || "",
        blistering_email: form.blistering_email?.trim() || "",
        address: form.address?.trim() || "",
        asl_reference: form.asl_reference?.trim() || "",
        notes: form.notes?.trim() || "",
        is_default: !!form.is_default,
      };
      let response;
      if (editingDoctor?.id) {
        response = await axios.put(
          `${API_URL}${apiBase}/${editingDoctor.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Medico aggiornato");
      } else {
        response = await axios.post(`${API_URL}${apiBase}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Medico aggiunto all'anagrafica");
      }
      onSaved?.(response.data);
      handleOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio del medico");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingDoctor ? "Modifica Medico" : title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Nome e Cognome *</Label>
            <Input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Es. Dr.ssa Gabriella Di Rollo"
            />
          </div>
          <div>
            <Label>Specializzazione</Label>
            <Input
              value={form.specialization}
              onChange={(e) => setField("specialization", e.target.value)}
              placeholder="Es. Medico di Medicina Generale MMG"
            />
          </div>
          <div>
            <Label>Telefono</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="Es. +39 ..."
            />
          </div>
          <div>
            <Label>Email personale</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="Email personale del medico"
            />
          </div>
          <div>
            <Label>Email comunicazioni blisteraggio</Label>
            <Input
              type="email"
              value={form.blistering_email}
              onChange={(e) => setField("blistering_email", e.target.value)}
              placeholder="Email per comunicazioni blisteraggio"
            />
          </div>
          <div>
            <Label>Indirizzo studio</Label>
            <Input
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              placeholder="Via, CAP, città dello studio medico"
            />
          </div>
          <div>
            <Label>ASL di riferimento</Label>
            <Input
              value={form.asl_reference}
              onChange={(e) => setField("asl_reference", e.target.value)}
              placeholder="Es. ASL Roma 1"
            />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Note aggiuntive"
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
